import { useState } from 'react';
import { Loader2, Clapperboard } from 'lucide-react';
import { storyboardApi } from './storyboardApi';
import SceneCard from './SceneCard';
import CampaignPackage from './campaign/CampaignPackage';

export default function ScenesGrid({ board, scenes, authHeader, onScenesChange, onBoardChange }) {
  const [numScenes, setNumScenes] = useState(0); // 0 = auto (AI decides 5-15)
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');

  const approved = board.status === 'ready' || board.status === 'complete' || board.status === 'failed';
  const generating = board.status === 'generating';
  const done = scenes.filter(s => s.status === 'done').length;
  const hasScenes = scenes.length > 0;

  const launch = async () => {
    if (hasScenes && !generating) {
      const sure = window.confirm('This will replace all current scenes with a new set. Continue?');
      if (!sure) return;
    }
    setError('');
    setLaunching(true);
    const { ok, data } = await storyboardApi.generateScenes(board.id, numScenes || null, authHeader);
    setLaunching(false);
    if (ok) {
      // Job started: planning + images run in the background, progress arrives via polling.
      onScenesChange([]);
      onBoardChange({ ...board, status: 'generating', error_message: null });
    } else {
      setError(typeof data?.detail === 'string' ? data.detail : 'Scene generation failed');
    }
  };

  const regenerate = async (scene) => {
    onScenesChange(scenes.map(s => (s.id === scene.id ? { ...s, status: 'pending', image_url: null } : s)));
    const { ok, data } = await storyboardApi.regenerateScene(board.id, scene.id, authHeader);
    if (!ok) setError(typeof data?.detail === 'string' ? data.detail : 'Regeneration failed');
  };

  return (
    <div className="space-y-4" data-testid="sb-scenes">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="sb-generate-scenes-btn"
          onClick={launch}
          disabled={!approved || generating || launching}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {launching || generating
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Clapperboard className="w-3.5 h-3.5" />}
          <span>{launching ? 'Starting...' : generating ? (hasScenes ? `Generating ${done}/${scenes.length}` : 'Planning scenes...') : hasScenes ? 'Re-generate all scenes' : 'Generate scenes'}</span>
        </button>

        <select
          data-testid="sb-num-scenes"
          value={numScenes}
          onChange={e => setNumScenes(Number(e.target.value))}
          disabled={generating || launching}
          className="px-3 py-2 rounded-xl bg-white/40 border border-gray-200/40 text-xs text-gray-700 outline-none disabled:opacity-50"
        >
          <option value={0} label="Auto (AI decides 5-15)" />
          {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(n => <option key={n} value={n} label={`${n} scenes`} />)}
        </select>

        {!approved && !generating && (
          <span className="text-[11px] text-gray-400" data-testid="sb-approve-hint">
            Approve the creative concept first to unlock scene generation.
          </span>
        )}
      </div>

      {/* Progress bar */}
      {generating && hasScenes && (
        <div className="h-1 rounded-full bg-gray-200/60 overflow-hidden" data-testid="sb-progress">
          <div
            className="h-full bg-gray-900 rounded-full transition-all duration-700"
            style={{ width: `${Math.max(4, (done / scenes.length) * 100)}%` }}
          />
        </div>
      )}

      {error && <p className="text-xs text-red-500" data-testid="sb-scenes-error">{error}</p>}
      {board.status === 'failed' && board.error_message && (
        <p className="text-xs text-red-500" data-testid="sb-job-error">
          Generation failed: {board.error_message} — you can retry with "Generate scenes".
        </p>
      )}

      {/* Grid */}
      {hasScenes ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-6">
          {scenes.map(scene => (
            <SceneCard key={scene.id} scene={scene} onRegenerate={() => regenerate(scene)} />
          ))}
        </div>
      ) : generating ? (
        <div className="rounded-2xl border border-dashed border-gray-200/70 bg-white/30 p-10 text-center" data-testid="sb-planning">
          <Loader2 className="w-8 h-8 text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-gray-500 font-medium">Planning your storyboard...</p>
          <p className="text-xs text-gray-400 mt-1">
            The creative director is deciding how many scenes this story needs (5-15) and writing them (~1-2 min). Images will appear here one by one.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-200/70 bg-white/30 p-10 text-center">
          <Clapperboard className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 font-medium">No scenes yet</p>
          <p className="text-xs text-gray-400 mt-1">
            {approved
              ? 'Launch generation to produce your full storyboard (5-15 consistent scenes — the AI picks the right count).'
              : 'Approve the concept in step 2 to enable generation.'}
          </p>
        </div>
      )}

      {/* Campaign Package — full deliverables below the scenes grid */}
      {hasScenes && !generating && (
        <CampaignPackage board={board} authHeader={authHeader} onBoardChange={onBoardChange} />
      )}
    </div>
  );
}
